import React, { useState, useMemo, useEffect } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DollarSign, TrendingUp, Calendar, RefreshCw, Building, Info, Plus, Trash2, ListPlus, Landmark, HardHat, Home, Calculator, User, MapPin, ChevronDown, UserPlus, Key, BarChart3 } from 'lucide-react';
import CurrencyInput from './CurrencyInput';
import ClientModal from '../ClientModal';
import VeiculoFipeCard from './VeiculoFipeCard';
import api from '../../utils/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const formatCurrency = (value) => {
  if (!value && value !== 0) return '0,00';
  return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseCurrencyValue = (value) => {
  if (!value) return 0;
  const str = value.toString().trim();
  if (!str) return 0;
  // Formato brasileiro completo (1.000.000,50)
  if (str.includes(',') && str.includes('.')) {
    return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
  }
  // Apenas vírgula → decimal brasileiro (1000,50)
  if (str.includes(',')) {
    return parseFloat(str.replace(',', '.')) || 0;
  }
  // Apenas ponto → SEMPRE separador de milhar no contexto BR
  // (ex.: "20.000" deve ser 20000, NÃO 20.0)
  // O sistema não recebe ponto como decimal porque o input limpa esse caractere.
  if (str.includes('.')) {
    return parseFloat(str.replace(/\./g, '')) || 0;
  }
  // Caso contrário, número puro (20000)
  return parseFloat(str) || 0;
};

const formatPercentage = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // Arredonda para no máximo 2 casas decimais para evitar 53.333333...
  const rounded = Math.round(num * 100) / 100;
  return String(rounded).replace('.', ',');
};

// Estado para controlar edição de campos percentuais
const usePercentageEdit = () => {
  const [editingPercField, setEditingPercField] = useState(null);
  const [editingPercValue, setEditingPercValue] = useState('');

  const getPercValue = (field, formValue) => {
    if (editingPercField === field) return editingPercValue;
    return formatPercentage(formValue);
  };

  const onPercFocus = (field, formValue) => {
    setEditingPercField(field);
    setEditingPercValue(formValue ? String(formValue).replace('.', ',') : '');
  };

  const onPercChange = (value) => {
    const cleaned = value.replace(/[^\d,]/g, '');
    const parts = cleaned.split(',');
    const sanitized = parts.length > 2 ? parts[0] + ',' + parts[1] : cleaned;
    setEditingPercValue(sanitized);
  };

  const getPercNumericValue = () => {
    const numStr = editingPercValue.replace(',', '.');
    return numStr === '' ? 0 : parseFloat(numStr) || 0;
  };

  const clearPerc = () => {
    setEditingPercField(null);
    setEditingPercValue('');
  };

  return { editingPercField, getPercValue, onPercFocus, onPercChange, getPercNumericValue, clearPerc };
};

export const InvestmentForm = ({ formData, setFormData, onCalculate, loading, tipoSimulacao = 'lancamento' }) => {
  const prazos = [36, 48, 60, 120];
  const [atoMode, setAtoMode] = useState(formData.ato_valor > 0 ? 'valor' : 'percentual');
  const [parcelaMode, setParcelaMode] = useState('valor'); // 'valor' | 'percentual'
  const [prazoCustom, setPrazoCustom] = useState(!prazos.includes(formData.prazo_meses));
  const [fluxoOpen, setFluxoOpen] = useState(true); // accordion do Fluxo de Pagamento
  const [divisaoCustom, setDivisaoCustom] = useState(false); // dividir entrada personalizado
  const [valoriacaoAILoading, setValorizacaoAILoading] = useState(false); // pesquisa valorização
  const [valorizacaoAIOpen, setValorizacaoAIOpen] = useState(false);
  const [valorizacaoAIForm, setValorizacaoAIForm] = useState({ regiao: '', tipo: 'apartamento', padrao: 'medio' });

  // Derivados de tipo de simulação
  const isProto = tipoSimulacao === 'pronto'; // imóvel pronto: sem obra, sem INCC/CUB
  const temObra = !isProto; // lançamento ou pré-lançamento: tem conclusão de obra
  const { editingPercField, getPercValue, onPercFocus, onPercChange, getPercNumericValue, clearPerc } = usePercentageEdit();
  // Formata número como percentual em pt-BR (vírgula decimal). Ex: 33.333 -> "33,3"
  const fmtPct = (n, digits = 1) => {
    const num = Number(n);
    if (!isFinite(num)) return '0';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };


  // Clientes cadastrados
  const [clientesCadastrados, setClientesCadastrados] = useState([]);
  const [clienteSugestoes, setClienteSugestoes] = useState([]);
  const [showClienteSugestoes, setShowClienteSugestoes] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/clients', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setClientesCadastrados((res.data || []).filter(c => c && c.name)))
        .catch(() => {});
    }
  }, []);
  useEffect(() => {
    const close = () => setShowClienteSugestoes(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // Empreendimentos cadastrados
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [unidadesEmp, setUnidadesEmp] = useState([]);
  const [calculoLivre, setCalculoLivre] = useState(false);
  useEffect(() => {
    // Tenta primeiro o endpoint público (mais rápido/sem auth).
    // Fallback para o autenticado se o público não existir (404) — é o caso do VPS antigo.
    (async () => {
      try {
        const res = await api.get('/public/empreendimentos');
        const data = res.data;
        setEmpreendimentos(data.items || data || []);
      } catch (e) {
        try {
          const res2 = await api.get('/empreendimentos?limit=500');
          const data = res2.data;
          setEmpreendimentos(data.items || data || []);
        } catch (_) {}
      }
    })();
  }, []);

  // Buscar unidades quando empreendimento mudar
  useEffect(() => {
    if (!formData.empreendimento || calculoLivre) {
      setUnidadesEmp([]);
      return;
    }
    const emp = empreendimentos.find(e => e.nome === formData.empreendimento);
    if (emp) {
      api.get(`/unidades?empreendimento_id=${emp.id}&status=disponivel&limit=500`).then(res => {
        setUnidadesEmp(res.data.items || []);
      }).catch(() => setUnidadesEmp([]));
    }
  }, [formData.empreendimento, empreendimentos, calculoLivre]);

  // ────────── ÍNDICES OFICIAIS (CUB / INCC / IPCA / IGP-M) ──────────
  // Busca o histórico real e calcula o acumulado 12 meses (taxa a.a.)
  const [indicesData, setIndicesData] = useState({ CUB: null, INCC: null, IPCA: null, 'IGP-M': null });
  const [indicesLoading, setIndicesLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };

      const computeMonthlyComposed = (items) => {
        if (!items || items.length === 0) return null;
        // ordena DESC por (ano, mes); pega últimos 12
        const sorted = [...items].sort((a, b) => (b.ano - a.ano) * 100 + (b.mes - a.mes));
        const last12 = sorted.slice(0, 12);
        if (last12.length < 1) return null;
        const fator = last12.reduce((acc, it) => acc * (1 + (Number(it.valor) || 0) / 100), 1);
        return {
          taxa_12m: (fator - 1) * 100,
          ultimo_mes: last12[0]?.mes_referencia || null,
          ultimo_valor: last12[0]?.valor ?? null,
          n_meses: last12.length,
        };
      };

      const computeCubByValue = async () => {
        try {
          const r = await api.get('/indices/cub/historico?limit=14', { headers });
          const items = r.data || [];
          if (!Array.isArray(items) || items.length < 2) return null;
          // ordena por created_at desc (já vem do backend) — usa atual e o de ~12 meses atrás
          const atual = items[0];
          const idx12 = Math.min(items.length - 1, 12);
          const antigo = items[idx12];
          if (!atual?.valor || !antigo?.valor) return null;
          return {
            taxa_12m: (atual.valor / antigo.valor - 1) * 100,
            ultimo_mes: atual.mes_referencia,
            ultimo_valor: atual.valor,
            n_meses: idx12,
          };
        } catch { return null; }
      };

      try {
        const [ipcaH, inccH, igpmH, cubData] = await Promise.allSettled([
          api.get('/indices/ipca/historico?anos=2', { headers }).then(r => r.data),
          api.get('/indices/incc/historico?anos=2', { headers }).then(r => r.data),
          api.get('/indices/igpm/historico?anos=2', { headers }).then(r => r.data),
          computeCubByValue(),
        ]);
        if (cancelled) return;
        const next = {
          IPCA: ipcaH.status === 'fulfilled' ? computeMonthlyComposed(ipcaH.value) : null,
          INCC: inccH.status === 'fulfilled' ? computeMonthlyComposed(inccH.value) : null,
          'IGP-M': igpmH.status === 'fulfilled' ? computeMonthlyComposed(igpmH.value) : null,
          CUB: cubData.status === 'fulfilled' ? cubData.value : null,
        };
        setIndicesData(next);
      } finally {
        if (!cancelled) setIndicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-preenche a taxa quando o usuário muda o índice E o campo ainda está vazio (ou era da taxa anterior)
  useEffect(() => {
    const idxConstr = formData.correcao_construcao_indice || 'CUB';
    const dados = indicesData[idxConstr];
    if (dados && (!formData.correcao_construcao_taxa || formData._auto_construcao === idxConstr)) {
      setFormData(prev => ({
        ...prev,
        correcao_construcao_taxa: Number(dados.taxa_12m.toFixed(2)),
        _auto_construcao: idxConstr,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicesData, formData.correcao_construcao_indice]);

  useEffect(() => {
    const idxPos = formData.correcao_pos_indice || 'IPCA';
    const dados = indicesData[idxPos];
    if (dados && (!formData.correcao_pos_taxa || formData._auto_pos === idxPos)) {
      setFormData(prev => ({
        ...prev,
        correcao_pos_taxa: Number(dados.taxa_12m.toFixed(2)),
        _auto_pos: idxPos,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicesData, formData.correcao_pos_indice]);

  // Estado para Financiamento Pós Obra (Bancário ou Construtora/Direto)
  const [tipoFinanciamento, setTipoFinanciamento] = useState(formData.tipo_financiamento || 'bancario');
  const [financiamentoMode, setFinanciamentoMode] = useState(formData.financiamento_pos_obra_percentual > 0 ? 'percentual' : 'valor');
  // Estado para Parcela das Chaves (balão de entrega)
  const [parcelaChavesMode, setParcelaChavesMode] = useState(formData.parcela_chaves_percentual > 0 ? 'percentual' : 'valor');

  // Sincroniza tipo_financiamento inicial no formData
  useEffect(() => {
    if (formData.tipo_financiamento !== tipoFinanciamento) {
      setFormData((prev) => ({ ...prev, tipo_financiamento: tipoFinanciamento }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Estado de foco/blur para campos de moeda - mostra número bruto ao focar, formatado ao desfocar
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  const handleCurrencyFocus = (field) => {
    setEditingField(field);
    const val = formData[field];
    // Mostra valor formatado com milhar e centavos ao focar
    if (val) {
      const formatted = formatCurrency(val);
      setEditValue(formatted);
    } else {
      setEditValue('');
    }
  };

  const handleCurrencyInput = (value) => {
    // Remove tudo exceto dígitos e vírgula
    let cleaned = value.replace(/[^\d,]/g, '');
    // Garante apenas uma vírgula e max 2 decimais
    const parts = cleaned.split(',');
    if (parts.length > 2) {
      cleaned = parts[0] + ',' + parts[1];
    } else if (parts.length === 2) {
      cleaned = parts[0] + ',' + parts[1].slice(0, 2);
    }
    // Formata parte inteira com pontos de milhar
    const [inteiro, decimal] = cleaned.split(',');
    const intFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    setEditValue(decimal !== undefined ? intFormatado + ',' + decimal : intFormatado);
  };

  const handleCurrencyBlur = (field) => {
    // Converte para número com centavos
    const num = parseCurrencyValue(editValue) || 0;
    setFormData((prev) => ({ ...prev, [field]: num }));
    setEditingField(null);
    setEditValue('');
  };

  const getCurrencyValue = (field) => {
    // Se está editando, retorna o valor bruto sem formatação
    if (editingField === field) return editValue;
    // Senão, retorna o valor formatado
    const rawValue = formData[field] || 0;
    return formatCurrency(rawValue);
  };

  const handleCurrencyChange = (field, value) => {
    const numericValue = parseCurrencyValue(value);
    setFormData({ ...formData, [field]: numericValue });
  };

  const handlePercentageChange = (field, value) => {
    onPercChange(value);
  };

  const handlePercentageBlur = (field) => {
    const num = getPercNumericValue();
    setFormData(prev => ({ ...prev, [field]: num }));
    clearPerc();
  };

  const handleNumberChange = (field, value) => {
    const num = parseInt(value) || 0;
    setFormData({ ...formData, [field]: num });
  };

  const handlePrazoClick = (prazo) => {
    setFormData({ ...formData, prazo_meses: prazo });
  };

  // Reforço custom handlers
  const addReforcoCustom = () => {
    const current = formData.reforcos_custom || [];
    setFormData({
      ...formData,
      reforcos_custom: [...current, { mes: 12, valor: 10000 }],
    });
  };

  const updateReforcoCustom = (index, field, value) => {
    const current = [...(formData.reforcos_custom || [])];
    if (field === 'mes') {
      current[index] = { ...current[index], mes: parseInt(value) || 0 };
    } else {
      current[index] = { ...current[index], valor: parseCurrencyValue(value) };
    }
    setFormData({ ...formData, reforcos_custom: current });
  };

  // Editing state for custom reforço value fields
  const [editingReforcoIdx, setEditingReforcoIdx] = useState(null);
  const [editReforcoValue, setEditReforcoValue] = useState('');

  const handleReforcoFocus = (index) => {
    setEditingReforcoIdx(index);
    const val = (formData.reforcos_custom || [])[index]?.valor;
    setEditReforcoValue(val ? formatCurrency(val) : '');
  };

  const handleReforcoInput = (value, index) => {
    let cleaned = value.replace(/[^\d,]/g, '');
    const parts = cleaned.split(',');
    if (parts.length > 2) {
      cleaned = parts[0] + ',' + parts[1];
    } else if (parts.length === 2) {
      cleaned = parts[0] + ',' + parts[1].slice(0, 2);
    }
    const [inteiro, decimal] = cleaned.split(',');
    const intFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const formatted = decimal !== undefined ? intFormatado + ',' + decimal : intFormatado;
    setEditReforcoValue(formatted);
    // Atualizar valor em tempo real
    const num = parseCurrencyValue(formatted);
    if (num > 0 && index !== undefined) {
      const current = [...(formData.reforcos_custom || [])];
      current[index] = { ...current[index], valor: num };
      setFormData(prev => ({ ...prev, reforcos_custom: current }));
    }
  };

  const handleReforcoBlur = (index) => {
    const num = parseCurrencyValue(editReforcoValue);
    const current = [...(formData.reforcos_custom || [])];
    current[index] = { ...current[index], valor: num };
    setFormData({ ...formData, reforcos_custom: current });
    setEditingReforcoIdx(null);
    setEditReforcoValue('');
  };

  const removeReforcoCustom = (index) => {
    const current = [...(formData.reforcos_custom || [])];
    current.splice(index, 1);
    setFormData({ ...formData, reforcos_custom: current });
  };

  const setReforcoMode = (mode) => {
    const updates = { modo_reforco: mode };
    // Limpar apenas campos específicos do modo anterior, NUNCA a data de conclusão
    if (mode === 'baloes') {
      updates.reforcos_custom = [];
      updates.reforco_anual = 0;
      updates.distribuir_config = null;
    } else if (mode === 'anual') {
      updates.reforcos_custom = [];
      updates.distribuir_config = null;
      // Sugerir valor anual baseado na conclusão da obra
      if (formData.conclusao_obra && formData.data_inicio && formData.valor_compra > 0) {
        const saldo = Math.max(0, formData.valor_compra - (formData.ato_valor || 0) - ((formData.parcela_mensal || 0) * Math.max(0, (formData.prazo_meses || 120) - (formData.parcela_inicio_mes || 1) + 1)));
        const [anoI, mesI] = formData.data_inicio.split('-').map(Number);
        const [anoC, mesC] = formData.conclusao_obra.split('-').map(Number);
        const mesesConst = (anoC - anoI) * 12 + (mesC - mesI);
        const numAnos = Math.max(1, Math.floor(mesesConst / 12));
        if (saldo > 0 && numAnos > 0) {
          updates.reforco_anual = Math.round(saldo / numAnos * 100) / 100;
        }
      }
    } else if (mode === 'custom') {
      updates.reforco_anual = 0;
      updates.distribuir_config = null;
    } else if (mode === 'distribuir') {
      updates.reforcos_custom = [];
      updates.reforco_anual = 0;
      updates.distribuir_config = formData.distribuir_config || {
        tipo_valor: 'valor', // 'valor' ou 'percentual'
        valor: 0,
        percentual: 0,
        frequencia: 'semestral', // 'trimestral', 'semestral', 'anual'
        periodo: 'construcao', // 'construcao' ou 'total'
      };
    }
    setFormData({ ...formData, ...updates });
  };

  // Calcular meses de construção e marcos
  const construcaoInfo = useMemo(() => {
    if (!formData.conclusao_obra || !formData.data_inicio) return null;
    try {
      const [anoI, mesI] = formData.data_inicio.split('-').map(Number);
      const [anoC, mesC] = formData.conclusao_obra.split('-').map(Number);
      const mesesConstrucao = (anoC - anoI) * 12 + (mesC - mesI);
      if (mesesConstrucao <= 0) return null;
      const meioTorre = Math.round(mesesConstrucao / 2);
      return { mesesConstrucao, meioTorre, chaves: mesesConstrucao };
    } catch {
      return null;
    }
  }, [formData.data_inicio, formData.conclusao_obra]);

  // Calculate preview - GARANTIR QUE TODOS SÃO NÚMEROS
  const valorCompra = Number(formData.valor_compra) || 0;
  const atoSinal = atoMode === 'valor'
    ? (Number(formData.ato_valor) || 0)
    : valorCompra * ((Number(formData.entrada_percentual) || 0) / 100);
  const parcelaMensal = Number(formData.parcela_mensal) || 0;
  const prazo = Number(formData.prazo_meses) || 100;
  const parcelaInicio = Number(formData.parcela_inicio_mes) || 1;
  // Parcelas mensais cobrem TODO o prazo selecionado pelo usuário,
  // independente do tipo de financiamento (bancário/direto) ou do período de obra.
  const mesFimParcelaGlobal = prazo;
  const mesesComParcela = Math.max(0, mesFimParcelaGlobal - parcelaInicio + 1);
  const parcelasTotal = parcelaMensal * mesesComParcela;

  const modoReforco = formData.modo_reforco || 'baloes';

  let reforcosTotal = 0;
  if (formData.reforcos_custom) {
    reforcosTotal = formData.reforcos_custom.reduce((sum, r) => sum + (Number(r.valor) || 0), 0);
  }

  // Financiamento Bancário ENTRA no cálculo total SOMENTE quando tipo_financiamento === 'bancario'.
  // No modo "Financiamento Direto" o valor preenchido no campo é ignorado (as parcelas continuam pós obra).
  const financiamentoRaw = financiamentoMode === 'valor'
    ? (Number(formData.financiamento_pos_obra) || 0)
    : valorCompra * ((Number(formData.financiamento_pos_obra_percentual) || 0) / 100);
  const financiamento = (formData.tipo_financiamento === 'bancario') ? financiamentoRaw : 0;

  // Parcela das Chaves (balão pago na entrega das chaves) - aceita valor OU percentual
  const parcelaChavesTotal = (() => {
    const v = Number(formData.parcela_chaves_valor) || 0;
    if (v > 0) return v;
    const p = Number(formData.parcela_chaves_percentual) || 0;
    if (p > 0 && valorCompra > 0) return (valorCompra * p) / 100;
    return 0;
  })();

  // Veículos no Negócio (cliente entrega veículos como parte do pagamento)
  const veiculosTotal = (formData.veiculos || []).reduce(
    (acc, v) => acc + (Number(v?.valor) || 0),
    0
  );

  // TOTAL DO INVESTIMENTO = Ato + Parcelas + Reforços + Parcela das Chaves + Veículos + (Financiamento Bancário se modo=bancario)
  const totalProposta = atoSinal + parcelasTotal + reforcosTotal + parcelaChavesTotal + veiculosTotal + financiamento;
  const atoPerc = valorCompra > 0 ? (atoSinal / valorCompra * 100) : 0;
  const parcelasPerc = valorCompra > 0 ? (parcelasTotal / valorCompra * 100) : 0;
  const reforcosPerc = valorCompra > 0 ? (reforcosTotal / valorCompra * 100) : 0;
  const parcelaChavesPerc = valorCompra > 0 ? (parcelaChavesTotal / valorCompra * 100) : 0;
  const veiculosPerc = valorCompra > 0 ? (veiculosTotal / valorCompra * 100) : 0;

  // ===== AUTO-AJUSTE DA PARCELA MENSAL =====
  // Quando parcela_auto é true, a parcela mensal é sempre recalculada para fechar o saldo
  // do valor de compra (valorCompra - ato - reforços - financiamento bancário).
  // Ativa ao clicar "Usar sugerida"; desativa ao o usuário editar manualmente.
  const tipoFinanciamentoAtual = formData.tipo_financiamento || 'direto';
  const financiamentoBancarioAuto = tipoFinanciamentoAtual === 'bancario' ? financiamento : 0;
  // MESES EFETIVOS DE PARCELA (depende do tipo de financiamento):
  // - BANCÁRIO: parcelas vão SÓ até a entrega das chaves; banco quita o saldo depois.
  // - DIRETO: parcelas vão até o prazo TOTAL; construtora financia o saldo via parcelas.
  // MESES EFETIVOS DE PARCELA: sempre usa o PRAZO TOTAL selecionado pelo usuário,
  // independente do tipo de financiamento ou do período de obra.
  const mesParcelaFim = formData.prazo_meses || 120;
  const numMesesParcelaAuto = Math.max(1, mesParcelaFim - (formData.parcela_inicio_mes || 1) + 1);
  // Parcela das Chaves (override) também sai do saldo a parcelar — aceita valor OU percentual
  const parcelaChavesValorAuto = parcelaChavesTotal;
  const saldoParcelasAuto = Math.max(
    0,
    valorCompra - atoSinal - reforcosTotal - financiamentoBancarioAuto - parcelaChavesValorAuto - veiculosTotal
  );
  const parcelaSugeridaAuto = Math.round((saldoParcelasAuto / numMesesParcelaAuto) * 100) / 100;

  useEffect(() => {
    if (!formData._parcela_auto) return;
    if (parcelaSugeridaAuto < 0) return;
    // Evita loop: só atualiza se o valor for diferente
    if (Math.abs((formData.parcela_mensal || 0) - parcelaSugeridaAuto) > 0.005) {
      setFormData(prev => ({ ...prev, parcela_mensal: parcelaSugeridaAuto }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelaSugeridaAuto, formData._parcela_auto]);

  const prazoMaxMeses = formData.idade_cliente
    ? Math.max(0, Math.floor((80.5 - Number(formData.idade_cliente)) * 12))
    : null;
  const prazoMaxAnos = prazoMaxMeses !== null ? Math.floor(prazoMaxMeses / 12) : null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Nome do Cliente e Empreendimento */}
      <Card className="bg-white border-stone-200 border-l-4 border-l-emerald-600">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-stone-900 font-heading">
            <User className="text-primary" size={20} />
            Cliente e Empreendimento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 relative">
              <Label htmlFor="nome_cliente" className="input-label">
                Nome do Cliente
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                  <User size={16} />
                </span>
                <Input
                  id="nome_cliente"
                  data-testid="input-nome-cliente"
                  type="text"
                  value={formData.nome_cliente || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({ ...formData, nome_cliente: val });
                    if (val.length >= 2) {
                      const filtered = clientesCadastrados.filter(c =>
                        c && c.name && c.name.toLowerCase().includes(val.toLowerCase())
                      );
                      setClienteSugestoes(filtered);
                      setShowClienteSugestoes(true);
                    } else {
                      setShowClienteSugestoes(false);
                    }
                  }}
                  onFocus={() => {
                    if ((formData.nome_cliente || '').length >= 2) setShowClienteSugestoes(true);
                  }}
                  className="pl-10 h-11"
                  placeholder="Digite para buscar..."
                />
              </div>
              {showClienteSugestoes && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {clienteSugestoes.length > 0 ? (
                    clienteSugestoes.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex items-center gap-2"
                        onClick={() => {
                          setFormData({ ...formData, nome_cliente: c.name });
                          setShowClienteSugestoes(false);
                        }}
                      >
                        <User size={14} className="text-stone-400" />
                        <span>{c.name}</span>
                        {c.phone && <span className="text-xs text-stone-400 ml-auto">{c.phone}</span>}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-3 text-sm text-stone-500">
                      <p>Nenhum cliente encontrado</p>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShowClienteSugestoes(false); setShowClienteModal(true); }}
                        className="text-primary hover:underline text-xs mt-1 inline-flex items-center gap-1"
                      >
                        <UserPlus size={12} /> Cadastrar novo cliente
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="empreendimento" className="input-label">
                Empreendimento
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 z-10">
                  <MapPin size={16} />
                </span>
                <select
                  id="empreendimento"
                  data-testid="input-empreendimento"
                  value={formData.empreendimento || ''}
                  onChange={(e) => {
                    const nome = e.target.value;
                    const emp = empreendimentos.find(em => em.nome === nome);
                    const updates = { empreendimento: nome, numero_unidade: '' };
                    if (emp && emp.previsao_entrega) {
                      // Se já é formato YYYY-MM, usar direto; se é só YYYY, assumir dezembro
                      const prev = emp.previsao_entrega;
                      updates.conclusao_obra = /^\d{4}-\d{2}$/.test(prev) ? prev : /^\d{4}$/.test(prev) ? `${prev}-12` : '';
                    }
                    setFormData({ ...formData, ...updates });
                  }}
                  className="flex w-full rounded-md border border-input bg-transparent pl-10 pr-8 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm h-11 appearance-none cursor-pointer"
                >
                  <option value="">Selecione o empreendimento</option>
                  {empreendimentos.map((emp) => (
                    <option key={emp.id} value={emp.nome}>{emp.nome}</option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none">
                  <ChevronDown size={16} />
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_unidade" className="input-label">
                Número da Unidade
              </Label>
              <div className="flex gap-2">
                {calculoLivre ? (
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                      <Home size={16} />
                    </span>
                    <Input
                      id="numero_unidade"
                      data-testid="input-numero-unidade"
                      type="text"
                      value={formData.numero_unidade || ''}
                      onChange={(e) => setFormData({ ...formData, numero_unidade: e.target.value })}
                      className="pl-10 h-11"
                      placeholder="Ex: 101, 202"
                    />
                  </div>
                ) : (
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 z-10">
                      <Home size={16} />
                    </span>
                    <select
                      id="numero_unidade"
                      data-testid="input-numero-unidade"
                      value={formData._unidade_id || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        const unidade = unidadesEmp.find(u => u.id === id);
                        if (unidade) {
                          setFormData({
                            ...formData,
                            _unidade_id: id,
                            numero_unidade: unidade.numero,
                            valor_mercado: unidade.valor,
                            valor_compra: unidade.valor,
                          });
                        } else {
                          setFormData({ ...formData, _unidade_id: '', numero_unidade: '' });
                        }
                      }}
                      className="flex w-full rounded-md border border-input bg-transparent pl-10 pr-8 py-2 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm h-11 appearance-none cursor-pointer"
                    >
                      <option value="">Selecione a unidade</option>
                      {unidadesEmp.map((u) => {
                        const torreLabel = u.torre && u.torre !== 'Único' ? `Torre ${u.torre} · ` : (u.bloco ? `${u.bloco} · ` : '');
                        return (
                          <option key={u.id} value={u.id}>
                            {torreLabel}Apto {u.numero} | {u.tipologia} | {u.area_privativa}m²
                          </option>
                        );
                      })}
                    </select>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none">
                      <ChevronDown size={16} />
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="btn-calculo-livre"
                  onClick={() => {
                    setCalculoLivre(!calculoLivre);
                    if (!calculoLivre) {
                      setFormData({ ...formData, numero_unidade: '' });
                    }
                  }}
                  className={`h-11 px-4 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    calculoLivre
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'border border-input bg-transparent hover:bg-stone-100'
                  }`}
                >
                  Cálculo Livre
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Valores do Imóvel */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-stone-900 font-heading">
            <Building className="text-primary" size={20} />
            Valor do Imóvel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="valor_mercado" className="input-label">
                Valor de Tabela / Mercado / Avaliação
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                <Input
                  id="valor_mercado"
                  data-testid="input-valor-mercado"
                  type="text"
                  value={getCurrencyValue('valor_mercado')}
                  onFocus={() => handleCurrencyFocus('valor_mercado')}
                  onChange={(e) => handleCurrencyInput(e.target.value)}
                  onBlur={() => handleCurrencyBlur('valor_mercado')}
                  className="pl-10 h-11"
                  placeholder="500.000,00"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="valor_compra" className="input-label">
                Valor de Compra / Proposta
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                <Input
                  id="valor_compra"
                  data-testid="input-valor-compra"
                  type="text"
                  value={getCurrencyValue('valor_compra')}
                  onFocus={() => handleCurrencyFocus('valor_compra')}
                  onChange={(e) => handleCurrencyInput(e.target.value)}
                  onBlur={() => handleCurrencyBlur('valor_compra')}
                  className="pl-10 h-11"
                  placeholder="450.000,00"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Datas */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-stone-900 font-heading">
            <Calendar className="text-primary" size={20} />
            Datas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Prazo + Datas (esquerda)  |  Sinal & Ato (direita) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Datas */}
            <div className="space-y-4 rounded-lg border border-stone-200 bg-stone-50/40 p-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_inicio" className="input-label">Data do Primeiro Pagamento</Label>
                  <Input
                    id="data_inicio"
                    data-testid="input-data-inicio"
                    type="month"
                    value={formData.data_inicio}
                    onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                    className="h-11"
                  />
                </div>

                {/* Conclusão da Obra — só para lançamento/pré-lançamento */}
                {temObra && (<div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="conclusao_obra" className="input-label mb-0">
                      <HardHat size={14} className="inline mr-1 text-primary" />
                      Conclusão da Obra
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="text-stone-400 hover:text-stone-600"><Info size={14} /></button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Previsão de entrega das chaves. Define os marcos: início de obra, meio da torre e entrega. Gera os balões automaticamente.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    id="conclusao_obra"
                    data-testid="input-conclusao-obra"
                    type="month"
                    value={formData.conclusao_obra || ''}
                    onChange={(e) => {
                      const novaConclusao = e.target.value;
                      const updates = { conclusao_obra: novaConclusao };
                      if (novaConclusao && formData.data_inicio) {
                        try {
                          const [anoI, mesI] = formData.data_inicio.split('-').map(Number);
                          const [anoC, mesC] = novaConclusao.split('-').map(Number);
                          const mesesCalc = (anoC - anoI) * 12 + (mesC - mesI);
                          if (mesesCalc > 0 && (formData.prazo_meses || 0) < mesesCalc) {
                            updates.prazo_meses = mesesCalc;
                            setPrazoCustom(true);
                          }
                        } catch {}
                      }
                      setFormData({ ...formData, ...updates });
                    }}
                    className="h-11"
                    min={formData.data_inicio}
                  />
                </div>
                )} {/* fim conclusao_obra condicional */}
              </div>
            </div>

          </div>

          {/* Marcos da Construção */}
          {construcaoInfo && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Landmark size={16} className="text-amber-700" />
                <span className="text-sm font-semibold text-amber-800">Marcos da Construção</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="px-2 py-1 bg-amber-100 rounded text-amber-800">
                  Construção: {construcaoInfo.mesesConstrucao} meses
                </span>
                <span className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                  Meio da Torre: mês {construcaoInfo.meioTorre}
                </span>
                <span className="px-2 py-1 bg-primary/10 rounded text-primary">
                  Entrega Chaves: mês {construcaoInfo.chaves}
                </span>
                {/* Pontua se há parcelas após a entrega das chaves */}
                {(() => {
                  const prazo = Number(formData.prazo_meses || 0);
                  const chaves = Number(construcaoInfo.chaves || 0);
                  const posChavesMeses = prazo > chaves ? prazo - chaves : 0;
                  if (posChavesMeses <= 0) return null;
                  const tipoFin = formData.tipo_financiamento || 'direto';
                  return tipoFin === 'bancario' ? (
                    <span
                      data-testid="badge-pos-chaves-bancario"
                      className="px-2 py-1 bg-indigo-100 rounded text-indigo-800 font-medium"
                      title="No modo Bancário, o banco quita o saldo na entrega; o comprador não paga parcelas após as chaves."
                    >
                      🏦 Pós-Chaves: {posChavesMeses} meses (banco assume)
                    </span>
                  ) : (
                    <span
                      data-testid="badge-pos-chaves-direto"
                      className="px-2 py-1 bg-rose-100 rounded text-rose-800 font-medium animate-pulse"
                      title="Há parcelas mensais continuando após a entrega das chaves. Verifique se é o desejado."
                    >
                      ⚠️ Parcelas Pós-Chaves: {posChavesMeses} meses
                    </span>
                  );
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Valorização e Correção */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-stone-900 font-heading">
            <TrendingUp className="text-primary" size={20} />
            Valorização e Correção
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="valorizacao_anual" className="input-label mb-0">Valorização Anual</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-stone-400 hover:text-stone-600"><Info size={14} /></button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Mercado promete 20-25%. Use 10-15% para cenário conservador.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <button
                  type="button"
                  onClick={() => setValorizacaoAIOpen(v => !v)}
                  className="text-xs px-3 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors font-medium flex items-center gap-1"
                >
                  <Calculator size={12} />
                  Pesquisar
                </button>
              </div>
              <div className="relative">
                <Input
                  id="valorizacao_anual"
                  data-testid="input-valorizacao-anual"
                  type="text"
                  value={getPercValue('valorizacao_anual', formData.valorizacao_anual)}
                  onFocus={() => onPercFocus('valorizacao_anual', formData.valorizacao_anual)}
                  onBlur={() => handlePercentageBlur('valorizacao_anual')}
                  onChange={(e) => handlePercentageChange('valorizacao_anual', e.target.value)}
                  className="pr-12 h-11"
                  placeholder="12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">% ao ano</span>
              </div>

              {/* Painel de pesquisa de valorização por IA */}
              {valorizacaoAIOpen && (
                <div className="mt-2 p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-3">
                  <p className="text-xs font-semibold text-emerald-800">Projeção de Valorização — Pesquisa IA</p>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-600">Região / Bairro / Cidade</Label>
                      <input
                        type="text"
                        className="w-full h-9 border border-stone-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="Ex: Itapema - SC, Balneário Camboriú"
                        value={valorizacaoAIForm.regiao}
                        onChange={e => setValorizacaoAIForm(f => ({ ...f, regiao: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-stone-600">Tipo</Label>
                        <select
                          className="w-full h-9 border border-stone-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
                          value={valorizacaoAIForm.tipo}
                          onChange={e => setValorizacaoAIForm(f => ({ ...f, tipo: e.target.value }))}
                        >
                          <option value="apartamento">Apartamento</option>
                          <option value="casa">Casa</option>
                          <option value="terreno">Terreno</option>
                          <option value="comercial">Comercial</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-stone-600">Padrão</Label>
                        <select
                          className="w-full h-9 border border-stone-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
                          value={valorizacaoAIForm.padrao}
                          onChange={e => setValorizacaoAIForm(f => ({ ...f, padrao: e.target.value }))}
                        >
                          <option value="popular">Popular</option>
                          <option value="medio">Médio</option>
                          <option value="medio_alto">Médio-Alto</option>
                          <option value="alto">Alto</option>
                          <option value="luxo">Luxo</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!valorizacaoAIForm.regiao || valoriacaoAILoading}
                    onClick={async () => {
                      if (!valorizacaoAIForm.regiao) return;
                      setValorizacaoAILoading(true);
                      try {
                        const resp = await fetch('https://api.anthropic.com/v1/messages', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            model: 'claude-sonnet-4-6',
                            max_tokens: 1000,
                            messages: [{
                              role: 'user',
                              content: `Você é um especialista em mercado imobiliário brasileiro. Com base em dados históricos e tendências de mercado, estime a valorização anual projetada para o seguinte perfil de imóvel:

Região: ${valorizacaoAIForm.regiao}
Tipo: ${valorizacaoAIForm.tipo}
Padrão: ${valorizacaoAIForm.padrao}

Responda APENAS em JSON, sem texto adicional, no formato:
{
  "valorização_conservadora": <número percentual anual, ex: 8.5>,
  "valorização_moderada": <número percentual anual>,
  "valorização_otimista": <número percentual anual>,
  "justificativa": "<2-3 frases curtas sobre os drivers da região>",
  "aviso": "Projeção baseada em dados históricos e tendências. Não constitui garantia de rentabilidade."
}`
                            }]
                          })
                        });
                        const data = await resp.json();
                        const text = data.content?.[0]?.text || '{}';
                        const clean = text.replace(/```json|```/g, '').trim();
                        const parsed = JSON.parse(clean);
                        // Aplicar valor moderado como sugestão
                        if (parsed.valorização_moderada) {
                          handlePercentageChange('valorizacao_anual', String(parsed.valorização_moderada));
                        }
                        // Mostrar resultado inline
                        setValorizacaoAIForm(f => ({ ...f, _resultado: parsed }));
                      } catch (e) {
                        setValorizacaoAIForm(f => ({ ...f, _resultado: { erro: 'Não foi possível obter a projeção. Tente novamente.' } }));
                      } finally {
                        setValorizacaoAILoading(false);
                      }
                    }}
                    className="w-full py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {valoriacaoAILoading ? (
                      <><RefreshCw size={12} className="animate-spin" /> Pesquisando...</>
                    ) : (
                      <><Calculator size={12} /> Projetar valorização</>
                    )}
                  </button>

                  {valorizacaoAIForm._resultado && !valorizacaoAIForm._resultado.erro && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-1 text-center">
                        {[
                          { label: 'Conservador', key: 'valorização_conservadora', color: 'text-blue-700' },
                          { label: 'Moderado', key: 'valorização_moderada', color: 'text-emerald-700 font-bold' },
                          { label: 'Otimista', key: 'valorização_otimista', color: 'text-amber-700' },
                        ].map(c => (
                          <button
                            key={c.key}
                            type="button"
                            onClick={() => handlePercentageChange('valorizacao_anual', String(valorizacaoAIForm._resultado[c.key]))}
                            className="p-2 bg-white rounded-lg border border-stone-200 hover:border-emerald-400 transition-colors"
                          >
                            <p className={`text-sm ${c.color}`}>{valorizacaoAIForm._resultado[c.key]}%</p>
                            <p className="text-[10px] text-stone-500">{c.label}</p>
                          </button>
                        ))}
                      </div>
                      {valorizacaoAIForm._resultado.justificativa && (
                        <p className="text-[10px] text-stone-600 leading-snug">{valorizacaoAIForm._resultado.justificativa}</p>
                      )}
                      <p className="text-[10px] text-stone-400 italic">{valorizacaoAIForm._resultado.aviso}</p>
                    </div>
                  )}
                  {valorizacaoAIForm._resultado?.erro && (
                    <p className="text-xs text-red-600">{valorizacaoAIForm._resultado.erro}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Correção por Período */}
          <div className="border-t border-stone-200 pt-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={16} className="text-stone-500" />
              <span className="text-sm font-medium text-stone-700">Correção por Período</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Durante a Construção */}
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Período Obra</span>
                  {construcaoInfo && (
                    <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-medium">
                      {construcaoInfo.mesesConstrucao} meses
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="input-label text-xs mb-0">Índice de Correção</Label>
                  <div className="flex gap-0.5 bg-white rounded-md p-0.5">
                    {['CUB', 'INCC'].map(idx => (
                      <button
                        key={idx}
                        data-testid={`correcao-construcao-${idx.toLowerCase()}`}
                        onClick={() => setFormData({ ...formData, correcao_construcao_indice: idx })}
                        className={`text-xs px-3 py-1.5 rounded-md transition-all flex-1 text-center ${(formData.correcao_construcao_indice || 'CUB') === idx ? 'bg-amber-600 text-white shadow-sm' : 'text-stone-600 hover:bg-amber-100'}`}
                      >
                        {idx}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="input-label text-xs mb-0">Taxa Anual ({formData.correcao_construcao_indice || 'CUB'})</Label>
                  <div className="relative">
                    <Input
                      data-testid="input-correcao-construcao-taxa"
                      type="text"
                      value={getPercValue('correcao_construcao_taxa', formData.correcao_construcao_taxa)}
                      onFocus={() => onPercFocus('correcao_construcao_taxa', formData.correcao_construcao_taxa)}
                      onBlur={() => handlePercentageBlur('correcao_construcao_taxa')}
                      onChange={(e) => { setFormData(p => ({ ...p, _auto_construcao: null })); handlePercentageChange('correcao_construcao_taxa', e.target.value); }}
                      className="pr-12 h-9 text-sm"
                      placeholder="4.5"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">% a.a.</span>
                  </div>
                  {(() => {
                    const idx = formData.correcao_construcao_indice || 'CUB';
                    const d = indicesData[idx];
                    if (indicesLoading) return <p className="text-[10px] text-stone-400">Buscando taxa oficial...</p>;
                    if (!d) return <p className="text-[10px] text-amber-700">Taxa oficial indisponível — preencha manualmente.</p>;
                    const taxa = d.taxa_12m;
                    const isAuto = formData._auto_construcao === idx;
                    return (
                      <div className="flex items-center justify-between gap-2 text-[10px]">
                        <span className="text-amber-700">
                          Acumulado 12m oficial: <strong>{taxa.toFixed(2)}%</strong>
                          {d.ultimo_mes && <span className="text-amber-600/80"> · ref. {d.ultimo_mes}</span>}
                        </span>
                        {!isAuto && (
                          <button
                            type="button"
                            data-testid="btn-usar-taxa-construcao"
                            onClick={() => setFormData(p => ({ ...p, correcao_construcao_taxa: Number(taxa.toFixed(2)), _auto_construcao: idx }))}
                            className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 transition-colors font-medium"
                          >
                            Usar
                          </button>
                        )}
                        {isAuto && <span className="text-emerald-700 font-medium">✓ usando oficial</span>}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Após a Construção */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-blue-800 uppercase tracking-wider">Pós-Chaves</span>
                  {construcaoInfo && (
                    <span className="text-[10px] bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded-full font-medium">
                      {Math.max(0, prazo - construcaoInfo.mesesConstrucao)} meses
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="input-label text-xs mb-0">Índice de Correção</Label>
                  <div className="flex gap-0.5 bg-white rounded-md p-0.5">
                    {['IPCA', 'IGP-M'].map(idx => (
                      <button
                        key={idx}
                        data-testid={`correcao-pos-${idx.toLowerCase().replace('-','')}`}
                        onClick={() => setFormData({ ...formData, correcao_pos_indice: idx })}
                        className={`text-xs px-3 py-1.5 rounded-md transition-all flex-1 text-center ${(formData.correcao_pos_indice || 'IPCA') === idx ? 'bg-blue-600 text-white shadow-sm' : 'text-stone-600 hover:bg-blue-100'}`}
                      >
                        {idx}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="input-label text-xs mb-0">Taxa Anual ({formData.correcao_pos_indice || 'IPCA'} + Spread)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <Input
                        data-testid="input-correcao-pos-taxa"
                        type="text"
                        value={getPercValue('correcao_pos_taxa', formData.correcao_pos_taxa)}
                        onFocus={() => onPercFocus('correcao_pos_taxa', formData.correcao_pos_taxa)}
                        onBlur={() => handlePercentageBlur('correcao_pos_taxa')}
                        onChange={(e) => { setFormData(p => ({ ...p, _auto_pos: null })); handlePercentageChange('correcao_pos_taxa', e.target.value); }}
                        className="pr-12 h-9 text-sm"
                        placeholder="4.5"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">% a.a.</span>
                    </div>
                    <div className="relative">
                      <Input
                        data-testid="input-correcao-pos-spread"
                        type="text"
                        value={getPercValue('correcao_pos_spread', formData.correcao_pos_spread)}
                        onFocus={() => onPercFocus('correcao_pos_spread', formData.correcao_pos_spread)}
                        onBlur={() => handlePercentageBlur('correcao_pos_spread')}
                        onChange={(e) => handlePercentageChange('correcao_pos_spread', e.target.value)}
                        className="pr-12 h-9 text-sm"
                        placeholder="1"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">+ %</span>
                    </div>
                  </div>
                  {(() => {
                    const idx = formData.correcao_pos_indice || 'IPCA';
                    const d = indicesData[idx];
                    if (indicesLoading) return <p className="text-[10px] text-stone-400">Buscando taxa oficial...</p>;
                    if (!d) return <p className="text-[10px] text-blue-700">Taxa oficial indisponível — preencha manualmente.</p>;
                    const taxa = d.taxa_12m;
                    const isAuto = formData._auto_pos === idx;
                    return (
                      <div className="flex items-center justify-between gap-2 text-[10px]">
                        <span className="text-blue-700">
                          Acumulado 12m oficial: <strong>{taxa.toFixed(2)}%</strong>
                          {d.ultimo_mes && <span className="text-blue-600/80"> · ref. {d.ultimo_mes}</span>}
                        </span>
                        {!isAuto && (
                          <button
                            type="button"
                            data-testid="btn-usar-taxa-pos"
                            onClick={() => setFormData(p => ({ ...p, correcao_pos_taxa: Number(taxa.toFixed(2)), _auto_pos: idx }))}
                            className="px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
                          >
                            Usar
                          </button>
                        )}
                        {isAuto && <span className="text-emerald-700 font-medium">✓ usando oficial</span>}
                      </div>
                    );
                  })()}
                  <p className="text-[10px] text-blue-600">
                    Total: {fmtPct(((Number(formData.correcao_pos_taxa) || 0) + (Number(formData.correcao_pos_spread) || 0)), 1)}% ao ano
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rendimento com Locação */}
      <Card className="bg-white border-stone-200 border-l-4 border-l-blue-500">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-stone-900 font-heading">
            <Home className="text-blue-600" size={20} />
            Rendimento com Locação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
            Simule o rendimento do imóvel com locação após a entrega das chaves. Base sugerida: 0.5% ao mês do valor do imóvel.
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="locacao_percentual" className="input-label mb-0">Taxa de Locação</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-stone-400 hover:text-stone-600"><Info size={14} /></button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Percentual mensal sobre o valor do imóvel. Média de mercado: 0.4% a 0.6% ao mês.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="relative">
                <Input
                  id="locacao_percentual"
                  data-testid="input-locacao-percentual"
                  type="text"
                  value={getPercValue('locacao_percentual', formData.locacao_percentual || 0.5)}
                  onFocus={() => onPercFocus('locacao_percentual', formData.locacao_percentual || 0.5)}
                  onBlur={() => handlePercentageBlur('locacao_percentual')}
                  onChange={(e) => {
                    const raw = e.target.value.replace(',', '.');
                    const val = parseFloat(raw);
                    if (!isNaN(val) && val > 1) {
                      handlePercentageChange('locacao_percentual', '1');
                    } else {
                      handlePercentageChange('locacao_percentual', e.target.value);
                    }
                  }}
                  className="pr-14 h-11"
                  placeholder="0.5"
                  max="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">% ao mês</span>
              </div>
              {formData.valor_compra > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  Valor estimado: R$ {formatCurrency((formData.valor_compra * (formData.locacao_percentual || 0.5)) / 100)} /mês
                  {formData.conclusao_obra && (() => {
                    const [ano, mes] = formData.conclusao_obra.split('-').map(Number);
                    const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                    return ` (a partir de ${meses[mes - 1]}/${ano})`;
                  })()}
                </p>
              )}
              <p className="text-[10px] text-stone-400 mt-0.5">Máximo: 1% ao mês</p>
            </div>
            
            {/* Valor fixo de locação — apenas para imóvel pronto */}
            {isProto && (<div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="locacao_valor_fixo" className="input-label mb-0">Ou Valor Fixo (opcional)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-stone-400 hover:text-stone-600"><Info size={14} /></button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Se preferir, informe um valor fixo de aluguel. Se preenchido, ignora o percentual.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                <Input
                  id="locacao_valor_fixo"
                  data-testid="input-locacao-valor-fixo"
                  type="text"
                  value={editingField === 'locacao_valor_fixo' ? editValue : (formData.locacao_valor_fixo ? formatCurrency(formData.locacao_valor_fixo) : '')}
                  onFocus={() => handleCurrencyFocus('locacao_valor_fixo')}
                  onBlur={() => handleCurrencyBlur('locacao_valor_fixo')}
                  onChange={(e) => handleCurrencyInput(e.target.value)}
                  className="pl-10 h-11"
                  placeholder="0,00"
                />
              </div>
              <p className="text-xs text-stone-500">
                Início: mês seguinte à entrega das chaves
              </p>
            </div>)}{/* fim isProto valor fixo */}
          </div>
        </CardContent>
      </Card>


      {/* Taxas */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="pb-4 cursor-pointer select-none" onClick={() => setFluxoOpen(v => !v)}>
          <CardTitle className="flex items-center justify-between text-base text-stone-900 font-heading">
            <div className="flex items-center gap-2">
              <DollarSign className="text-primary" size={20} />
              Fluxo de Pagamento
            </div>
            <div className="flex items-center gap-2">
              {!fluxoOpen && (
                <span className="text-xs text-stone-400 font-normal">
                  Parcela · Reforços · Chaves
                </span>
              )}
              <ChevronDown
                size={16}
                className={`text-stone-400 transition-transform duration-200 ${fluxoOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </CardTitle>
        </CardHeader>
        {fluxoOpen && (
        <CardContent className="space-y-5">

          {/* ─── PRAZO & SINAL/ATO (movido do card Datas) ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start border-b border-stone-100 pb-5">
            {/* Prazo de Pagamento */}
            <div className="space-y-2">
              <Label className="input-label">Prazo de Pagamento</Label>
              <div className="grid grid-cols-4 gap-2">
                {prazos.map((p) => (
                  <button
                    key={p}
                    data-testid={`prazo-btn-${p}`}
                    onClick={() => { setPrazoCustom(false); handlePrazoClick(p); }}
                    className={`prazo-btn ${!prazoCustom && formData.prazo_meses === p ? 'active' : ''}`}
                  >
                    {p} meses
                  </button>
                ))}
                <button
                  data-testid="prazo-btn-custom"
                  onClick={() => setPrazoCustom(true)}
                  className={`prazo-btn col-span-4 ${prazoCustom ? 'active' : ''}`}
                >
                  Personalizado
                </button>
              </div>
              {prazoCustom && (
                <div className="flex items-center gap-2 max-w-xs pt-1">
                  <Input
                    data-testid="input-prazo-custom"
                    type="number"
                    min="1"
                    max="360"
                    value={formData.prazo_meses}
                    onChange={(e) => handleNumberChange('prazo_meses', e.target.value)}
                    className="h-9 w-24"
                    placeholder="Ex: 48 meses"
                  />
                  <span className="text-sm text-stone-500">meses</span>
                </div>
              )}
            </div>
          </div>

          {/* COLUNA DIREITA: Sinal & Ato */}
          <div className="space-y-4 rounded-lg border border-stone-200 bg-stone-50/40 p-4">
          {/* Header com toggle R$ / % */}
          <div className="flex items-center justify-between">
            <Label className="input-label mb-0 text-sm font-semibold text-stone-700">Sinal &amp; Ato</Label>
            <div className="inline-flex rounded-md border border-stone-200 bg-white p-0.5">
              <button
                data-testid="ato-mode-valor"
                onClick={() => { setAtoMode('valor'); setFormData({ ...formData, entrada_percentual: 0 }); }}
                className={`text-xs px-3 py-1 rounded transition-colors ${atoMode === 'valor' ? 'bg-emerald-700 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
                title="Ato em R$"
              >
                R$
              </button>
              <button
                data-testid="ato-mode-percentual"
                onClick={() => { setAtoMode('percentual'); setFormData({ ...formData, ato_valor: 0 }); }}
                className={`text-xs px-3 py-1 rounded transition-colors ${atoMode === 'percentual' ? 'bg-emerald-700 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
                title="Ato em %"
              >
                %
              </button>
            </div>
          </div>

          {/* Grid: Sinal | Ato (lado a lado em md+) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sinal (pré-contrato) — apenas R$ */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label className="input-label mb-0 text-xs">
                  Sinal <span className="text-[10px] font-normal text-stone-500">(pré-contrato)</span>
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-stone-400 hover:text-stone-600"><Info size={12} /></button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Valor pago ANTES da assinatura do contrato. Soma ao total investido no mês 0.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                <Input
                  data-testid="input-sinal-valor"
                  type="text"
                  value={getCurrencyValue('sinal_valor')}
                  onFocus={() => handleCurrencyFocus('sinal_valor')}
                  onChange={(e) => handleCurrencyInput(e.target.value)}
                  onBlur={() => handleCurrencyBlur('sinal_valor')}
                  className="pl-10 h-11"
                  placeholder="5.000,00"
                />
              </div>
              {formData.valor_compra > 0 && formData.sinal_valor > 0 && (
                <p className="text-[11px] text-stone-500">
                  Equivale a {((formData.sinal_valor / formData.valor_compra) * 100).toFixed(2)}% do valor de compra
                </p>
              )}
            </div>

            {/* Ato (assinatura) — R$ ou % conforme atoMode */}
            <div className="space-y-2">
              <Label className="input-label mb-0 text-xs">
                Ato <span className="text-[10px] font-normal text-stone-500">(assinatura)</span>
              </Label>
              {atoMode === 'valor' ? (
                <>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                    <Input
                      data-testid="input-ato-valor"
                      type="text"
                      value={getCurrencyValue('ato_valor')}
                      onFocus={() => handleCurrencyFocus('ato_valor')}
                      onChange={(e) => handleCurrencyInput(e.target.value)}
                      onBlur={() => handleCurrencyBlur('ato_valor')}
                      className="pl-10 h-11"
                      placeholder="80.000,00"
                    />
                  </div>
                  {formData.valor_compra > 0 && formData.ato_valor > 0 && (
                    <p className="text-[11px] text-stone-500">
                      Equivale a {fmtPct(((formData.ato_valor / formData.valor_compra) * 100), 1)}% do valor de compra
                      {formData.sinal_valor > 0 && (
                        <><br/><span className="text-emerald-700 font-medium">Pago na assinatura: R$ {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(Math.max(0, formData.ato_valor - formData.sinal_valor))}</span> <span className="text-stone-400">(sinal abatido)</span></>
                      )}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="relative">
                    <Input
                      data-testid="input-entrada"
                      type="text"
                      value={getPercValue('entrada_percentual', formData.entrada_percentual)}
                      onFocus={() => onPercFocus('entrada_percentual', formData.entrada_percentual)}
                      onBlur={() => handlePercentageBlur('entrada_percentual')}
                      onChange={(e) => handlePercentageChange('entrada_percentual', e.target.value)}
                      className="pr-8 h-11"
                      placeholder="10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
                  </div>
                  {formData.valor_compra > 0 && formData.entrada_percentual > 0 && (
                    <p className="text-[11px] text-stone-500">
                      Equivale a R$ {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format((formData.valor_compra * formData.entrada_percentual) / 100)}
                      {formData.sinal_valor > 0 && (
                        <><br/><span className="text-emerald-700 font-medium">Pago na assinatura: R$ {new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(Math.max(0, ((formData.valor_compra * formData.entrada_percentual) / 100) - formData.sinal_valor))}</span> <span className="text-stone-400">(sinal abatido)</span></>
                      )}
                    </p>
                  )}
                </>
              )}
              {/* atalhos removidos conforme solicitado */}
            </div>
          </div>

          {/* Dividir Entrada em N parcelas */}
          <div className="border-t border-stone-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="input-label mb-0 text-xs flex items-center gap-1">
                Dividir entrada em
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-stone-400 hover:text-stone-600"><Info size={12} /></button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Divide o ato/sinal em N parcelas mensais. A 1ª fica no ato; as demais entram como reforços nos meses seguintes.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
            </div>
            <div className="flex gap-2">
              {[2, 3].map(n => (
                <button
                  key={n}
                  type="button"
                  data-testid={`dividir-entrada-${n}x`}
                  onClick={() => {
                    setDivisaoCustom(false);
                    const limpos = (formData.reforcos_custom || []).filter(r => !r._divisao_ato);
                    setFormData({ ...formData, divisao_ato_n: n, reforcos_custom: limpos });
                  }}
                  className={`text-xs px-4 py-1.5 rounded border transition-colors ${
                    !divisaoCustom && (formData.divisao_ato_n || 1) === n
                      ? 'bg-emerald-700 text-white border-emerald-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700'
                  }`}
                >
                  {n}x
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDivisaoCustom(true)}
                className={`text-xs px-4 py-1.5 rounded border transition-colors ${
                  divisaoCustom
                    ? 'bg-emerald-700 text-white border-emerald-700'
                    : 'border-stone-200 bg-white text-stone-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700'
                }`}
              >
                Personalizado
              </button>
              {divisaoCustom && (
                <input
                  type="number"
                  min="2"
                  max="48"
                  className="w-16 h-8 text-xs border border-stone-200 rounded px-2 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="N"
                  onChange={(e) => {
                    const n = parseInt(e.target.value) || 2;
                    const limpos = (formData.reforcos_custom || []).filter(r => !r._divisao_ato);
                    setFormData({ ...formData, divisao_ato_n: n, reforcos_custom: limpos });
                  }}
                />
              )}
              {/* Reset para não dividir */}
              {(formData.divisao_ato_n || 1) > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    setDivisaoCustom(false);
                    const limpos = (formData.reforcos_custom || []).filter(r => !r._divisao_ato);
                    setFormData({ ...formData, divisao_ato_n: 1, reforcos_custom: limpos });
                  }}
                  className="text-xs px-2 py-1.5 rounded border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            {(() => {
              const n = Math.max(1, parseInt(formData.divisao_ato_n || 1));
              if (n <= 1) return null;
              const total = atoMode === 'valor'
                ? (formData.ato_valor || 0)
                : ((formData.valor_compra || 0) * (formData.entrada_percentual || 0) / 100);
              if (total <= 0) return null;
              const parcela = Math.round((total / n) * 100) / 100;
              const fmt = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <p className="text-xs text-emerald-700 mt-2">
                  Entrada em {n}x de R$ {fmt(parcela)} — total R$ {fmt(total)}
                </p>
              );
            })()}
          </div>

          {/* ─── VEÍCULOS NO NEGÓCIO ─── */}
          <VeiculoFipeCard formData={formData} setFormData={setFormData} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="parcela_mensal" className="input-label mb-0">Parcela Mensal</Label>
                <div className="flex gap-1" role="tablist" aria-label="Modo da parcela">
                  <button
                    type="button"
                    data-testid="parcela-mode-valor"
                    onClick={() => setParcelaMode('valor')}
                    className={`text-xs px-2 py-1 rounded ${parcelaMode === 'valor' ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}
                  >
                    R$
                  </button>
                  <button
                    type="button"
                    data-testid="parcela-mode-percentual"
                    onClick={() => setParcelaMode('percentual')}
                    className={`text-xs px-2 py-1 rounded ${parcelaMode === 'percentual' ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}
                  >
                    %
                  </button>
                </div>
              </div>

              {(() => {
                // Parcelas cobrem TODO o prazo selecionado, independente do tipo de financiamento
                // ou do período de obra (a parcela mensal é calculada para o prazo total).
                const tipoFinUI = (formData.tipo_financiamento || 'direto');
                const mesFimParcela = prazo;
                const numMesesParcela = Math.max(1, mesFimParcela - (formData.parcela_inicio_mes || 1) + 1);
                const parcelaMensal = formData.parcela_mensal || 0;
                const totalParcelas = parcelaMensal * numMesesParcela;
                const pctAtual = valorCompra > 0 ? (totalParcelas / valorCompra) * 100 : 0;
                // Desconta Financiamento Bancário (quando modo = bancario) e Parcela das Chaves do saldo a parcelar
                const financiamentoBancario = tipoFinUI === 'bancario' ? (Number(financiamento) || 0) : 0;
                const parcelaChavesValorUI = parcelaChavesTotal;
                const veiculosTotalUI = (formData.veiculos || []).reduce((acc, v) => acc + (Number(v?.valor) || 0), 0);
                const saldoParcelas = Math.max(0, valorCompra - atoSinal - reforcosTotal - financiamentoBancario - parcelaChavesValorUI - veiculosTotalUI);
                const parcelaSugerida = saldoParcelas / numMesesParcela;
                const pctSugerido = valorCompra > 0 ? (saldoParcelas / valorCompra) * 100 : 0;
                // DELTA: o que falta (ou excede) na parcela mensal atual para fechar o total
                // delta > 0: falta; delta < 0: excede; delta == 0: fechado
                const deltaMensal = Math.round((parcelaSugerida - parcelaMensal) * 100) / 100;
                const deltaTotal = Math.round(deltaMensal * numMesesParcela * 100) / 100;
                const deltaPct = valorCompra > 0 ? (deltaTotal / valorCompra) * 100 : 0;
                const fechado = Math.abs(deltaTotal) < 0.5; // tolerância de 50 centavos
                const aplicarPct = (pct) => {
                  const total = (valorCompra * pct) / 100;
                  const mensal = Math.round((total / numMesesParcela) * 100) / 100;
                  setFormData({ ...formData, parcela_mensal: mensal, _parcela_auto: false });
                };
                const ativarAuto = () => {
                  if (formData._parcela_auto) {
                    // Toggle: desativa mantendo o valor atual
                    setFormData({ ...formData, _parcela_auto: false });
                  } else {
                    setFormData({
                      ...formData,
                      parcela_mensal: Math.round(parcelaSugerida * 100) / 100,
                      _parcela_auto: true,
                    });
                  }
                };
                const autoAtivo = !!formData._parcela_auto;

                if (parcelaMode === 'valor') {
                  return (
                    <>
                      <div className="relative">
                        <CurrencyInput
                          id="parcela_mensal"
                          data-testid="input-parcela-mensal"
                          value={parcelaMensal}
                          onValueChange={(value) => setFormData({ ...formData, parcela_mensal: value, _parcela_auto: false })}
                          placeholder="Exp: R$ 3.000,00"
                          className={`h-11 ${autoAtivo ? 'pr-16 border-emerald-400' : ''}`}
                        />
                        {autoAtivo && (
                          <span data-testid="badge-parcela-auto" className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded px-1.5 py-0.5">
                            Auto
                          </span>
                        )}
                      </div>
                      {valorCompra > 0 && !fechado && (
                        <div className={`flex items-center justify-between gap-2 p-2 rounded border ${deltaTotal > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                          <p className={`text-xs ${deltaTotal > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                            {deltaTotal > 0 ? 'Falta' : 'Excede'}:{' '}
                            <strong>{deltaTotal > 0 ? '+' : '−'}R$ {Math.abs(deltaMensal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês</strong>
                            {' '}({deltaTotal > 0 ? '+' : '−'}R$ {Math.abs(deltaTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} a {deltaTotal > 0 ? 'completar' : 'reduzir'})
                            {' · '}parcela ideal: <strong>R$ {parcelaSugerida.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês</strong>
                          </p>
                          <button
                            type="button"
                            data-testid="btn-usar-parcela-sugerida"
                            onClick={ativarAuto}
                            className={`text-xs px-2 py-1 rounded border transition-colors whitespace-nowrap ${autoAtivo ? 'bg-emerald-700 text-white border-emerald-700' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          >
                            {autoAtivo ? 'Auto ativo' : (deltaTotal > 0 ? 'Completar' : 'Ajustar')}
                          </button>
                        </div>
                      )}
                      {valorCompra > 0 && fechado && parcelaMensal > 0 && (
                        <p className="text-xs text-emerald-700 font-medium">
                          ✓ Total fechado — parcela cobre exatamente o saldo não alocado.
                        </p>
                      )}
                      {valorCompra > 0 && parcelaMensal > 0 && (
                        <p className="text-xs text-stone-500">
                          Equivale a {fmtPct(pctAtual, 2)}% do valor de compra (R$ {totalParcelas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) · saldo total sugerido: {fmtPct(pctSugerido, 2)}%
                        </p>
                      )}
                      {autoAtivo && (
                        <p className="text-[11px] text-emerald-700">
                          ⚡ Auto-ajuste ativo: a parcela mensal é recalculada automaticamente quando Ato, Reforços ou Financiamento Bancário mudam.
                        </p>
                      )}
                    </>
                  );
                }

                // Modo percentual
                return (
                  <>
                    <div className="relative">
                      <Input
                        data-testid="input-parcelas-percentual"
                        type="text"
                        value={getPercValue('_parcelas_percentual', pctAtual)}
                        onFocus={() => onPercFocus('_parcelas_percentual', pctAtual)}
                        onChange={(e) => handlePercentageChange('_parcelas_percentual', e.target.value)}
                        onBlur={() => {
                          const num = getPercNumericValue();
                          clearPerc();
                          if (num >= 0) aplicarPct(num);
                        }}
                        className={`pr-8 h-11 ${fechado ? 'border-emerald-400' : (deltaTotal > 0 ? 'border-red-300' : 'border-amber-300')}`}
                        placeholder="20"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
                    </div>
                    {valorCompra > 0 && parcelaMensal > 0 && (
                      <p className="text-xs text-stone-500">
                        <strong className="text-stone-700">R$ {parcelaMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>/mês × {numMesesParcela} meses = R$ {totalParcelas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({fmtPct(pctAtual, 2)}% do valor de compra)
                      </p>
                    )}
                    <div className="flex gap-2">
                      {[10, 20, 30].map(pct => (
                        <button
                          key={pct}
                          type="button"
                          data-testid={`btn-parcelas-pct-${pct}`}
                          onClick={() => aplicarPct(pct)}
                          className="flex-1 text-xs py-1.5 rounded border border-stone-200 text-stone-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                    {valorCompra > 0 && !fechado && (
                      <div className={`flex items-center justify-between gap-2 p-2 rounded border ${deltaTotal > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                        <p className={`text-xs ${deltaTotal > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                          {deltaTotal > 0 ? 'Falta' : 'Excede'}:{' '}
                          <strong>{deltaTotal > 0 ? '+' : '−'}{fmtPct(Math.abs(deltaPct), 2)}%</strong>{' '}
                          ({deltaTotal > 0 ? '+' : '−'}R$ {Math.abs(deltaMensal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês · R$ {Math.abs(deltaTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total)
                        </p>
                        <button
                          type="button"
                          data-testid="btn-usar-parcela-sugerida-pct"
                          onClick={ativarAuto}
                          className={`text-xs px-2 py-1 rounded border transition-colors whitespace-nowrap ${autoAtivo ? 'bg-emerald-700 text-white border-emerald-700' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                        >
                          {autoAtivo ? 'Auto ativo' : (deltaTotal > 0 ? 'Completar' : 'Ajustar')}
                        </button>
                      </div>
                    )}
                    {valorCompra > 0 && fechado && parcelaMensal > 0 && (
                      <p className="text-xs text-emerald-700 font-medium">
                        ✓ Total fechado — parcela cobre exatamente o saldo não alocado.
                      </p>
                    )}
                    {valorCompra > 0 && pctSugerido > 0 && fechado === false && (
                      <p className="text-[11px] text-stone-400">Saldo total disponível para parcelas: {fmtPct(pctSugerido, 2)}%</p>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="parcela_inicio_mes" className="input-label mb-0">Início das Parcelas</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-stone-400 hover:text-stone-600"><Info size={14} /></button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Mês em que começam as parcelas. Use 1 se começam imediatamente.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="relative">
                <Input
                  id="parcela_inicio_mes"
                  data-testid="input-parcela-inicio"
                  type="number"
                  min="1"
                  value={formData.parcela_inicio_mes || 1}
                  onChange={(e) => handleNumberChange('parcela_inicio_mes', e.target.value)}
                  className="h-11"
                  placeholder="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs">mês</span>
              </div>
            </div>
          </div>
          </div>
        </CardContent>
        )}
      </Card>

      {/* ========== REFORÇOS · FINANCIAMENTO · ANÁLISE · CHAVES — 2 colunas ========== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="card-form">
        <CardContent className="p-6">
          {/* Reforços / Balões */}
          <div className="pt-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ListPlus size={18} className="text-primary" />
                <span className="font-medium text-stone-700">Reforços / Balões</span>
              </div>
            </div>

            <div className="space-y-4">
                {/* Distribuir Valor - Ferramenta de geração */}
                <div className="p-4 bg-violet-50 rounded-xl border border-violet-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-violet-800">Distribuir Valor</span>
                    <button
                      data-testid="btn-toggle-distribuir"
                      onClick={() => setFormData({ ...formData, _showDistribuir: !formData._showDistribuir })}
                      className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                    >
                      {formData._showDistribuir ? 'Ocultar' : 'Configurar'}
                    </button>
                  </div>

                  {formData._showDistribuir && (
                    <div className="space-y-3 pt-2 border-t border-violet-200">
                      {/* Tipo: Valor ou Percentual */}
                      <div className="flex items-center gap-3">
                        <Label className="input-label text-xs mb-0 w-24 shrink-0">Tipo</Label>
                        <div className="flex gap-0.5 bg-white rounded-md p-0.5">
                          <button
                            data-testid="distribuir-tipo-valor"
                            onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), tipo_valor: 'valor' } })}
                            className={`text-xs px-3 py-1 rounded-md transition-all ${(formData.distribuir_config?.tipo_valor || 'valor') === 'valor' ? 'bg-violet-700 text-white shadow-sm' : 'text-stone-600 hover:bg-stone-100'}`}
                          >
                            Valor (R$)
                          </button>
                          <button
                            data-testid="distribuir-tipo-percentual"
                            onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), tipo_valor: 'percentual' } })}
                            className={`text-xs px-3 py-1 rounded-md transition-all ${formData.distribuir_config?.tipo_valor === 'percentual' ? 'bg-violet-700 text-white shadow-sm' : 'text-stone-600 hover:bg-stone-100'}`}
                          >
                            Percentual (%)
                          </button>
                        </div>
                      </div>

                      {/* Campo valor/percentual */}
                      <div className="flex items-center gap-3">
                        <Label className="input-label text-xs mb-0 w-24 shrink-0">
                          {formData.distribuir_config?.tipo_valor === 'percentual' ? 'Percentual' : 'Valor'}
                        </Label>
                        {formData.distribuir_config?.tipo_valor === 'percentual' ? (
                          <div className="flex-1 space-y-1">
                            <div className="relative">
                              <Input
                                data-testid="input-distribuir-percentual"
                                type="text"
                                value={formData.distribuir_config?.percentual || ''}
                                onChange={(e) => {
                                  const v = e.target.value.replace(/[^\d.]/g, '');
                                  setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), percentual: v === '' ? '' : parseFloat(v) || 0 } });
                                }}
                                className="pr-8 h-9 text-sm"
                                placeholder="10"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs">%</span>
                            </div>
                            {formData.distribuir_config?.percentual > 0 && valorCompra > 0 && (
                              <p className="text-[10px] text-violet-700">
                                {formData.distribuir_config.percentual}% do valor de compra = <strong>R$ {(valorCompra * (formData.distribuir_config.percentual / 100)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> (total, dividido entre os reforços)
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex-1 space-y-1.5">
                            <div className="flex gap-0.5 bg-white rounded-md p-0.5 w-fit">
                              <button
                                data-testid="distribuir-modo-parcela"
                                onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), modo_valor: 'parcela' } })}
                                className={`text-xs px-3 py-1 rounded-md transition-all ${(formData.distribuir_config?.modo_valor || 'parcela') === 'parcela' ? 'bg-violet-700 text-white shadow-sm' : 'text-stone-600 hover:bg-stone-100'}`}
                              >
                                Por Parcela
                              </button>
                              <button
                                data-testid="distribuir-modo-total"
                                onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), modo_valor: 'total' } })}
                                className={`text-xs px-3 py-1 rounded-md transition-all ${formData.distribuir_config?.modo_valor === 'total' ? 'bg-violet-700 text-white shadow-sm' : 'text-stone-600 hover:bg-stone-100'}`}
                              >
                                Total
                              </button>
                            </div>
                            <CurrencyInput
                              data-testid="input-distribuir-valor"
                              value={formData.distribuir_config?.valor || 0}
                              onValueChange={(value) => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), valor: value } })}
                              className="h-9 text-sm"
                              placeholder={formData.distribuir_config?.modo_valor === 'total' ? '200.000,00' : '50.000,00'}
                            />
                          </div>
                        )}
                      </div>

                      {/* Frequência */}
                      <div className="flex items-center gap-3">
                        <Label className="input-label text-xs mb-0 w-24 shrink-0">Periodicidade</Label>
                        <div className="flex gap-0.5 bg-white rounded-md p-0.5">
                          {[
                            { value: 'trimestral', label: 'Trimestral' },
                            { value: 'quadrimestral', label: 'Quadrimestral' },
                            { value: 'semestral', label: 'Semestral' },
                            { value: 'anual', label: 'Anual' },
                          ].map(f => (
                            <button
                              key={f.value}
                              data-testid={`distribuir-freq-${f.value}`}
                              onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), frequencia: f.value } })}
                              className={`text-xs px-3 py-1 rounded-md transition-all ${(formData.distribuir_config?.frequencia || 'semestral') === f.value ? 'bg-violet-700 text-white shadow-sm' : 'text-stone-600 hover:bg-stone-100'}`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Período */}
                      <div className="flex items-center gap-3">
                        <Label className="input-label text-xs mb-0 w-24 shrink-0">Período</Label>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            data-testid="distribuir-periodo-construcao"
                            onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), periodo: 'construcao' } })}
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${(formData.distribuir_config?.periodo || 'construcao') === 'construcao' ? 'bg-violet-700 text-white border-violet-700 shadow-sm' : 'text-stone-600 border-stone-200 hover:bg-white'}`}
                          >
                            Construção{construcaoInfo ? ` (${construcaoInfo.mesesConstrucao}m)` : ''}
                          </button>
                          <button
                            data-testid="distribuir-periodo-total"
                            onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), periodo: 'total' } })}
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${formData.distribuir_config?.periodo === 'total' ? 'bg-violet-700 text-white border-violet-700 shadow-sm' : 'text-stone-600 border-stone-200 hover:bg-white'}`}
                          >
                            Todo Pagamento ({prazo}m)
                          </button>
                          <button
                            data-testid="distribuir-periodo-manual"
                            onClick={() => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), periodo: 'manual', meses_manual: formData.distribuir_config?.meses_manual || 36 } })}
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${formData.distribuir_config?.periodo === 'manual' ? 'bg-violet-700 text-white border-violet-700 shadow-sm' : 'text-stone-600 border-stone-200 hover:bg-white'}`}
                          >
                            Manual
                          </button>
                        </div>
                      </div>
                      {formData.distribuir_config?.periodo === 'manual' && (
                        <div className="flex items-center gap-3">
                          <Label className="input-label text-xs mb-0 w-24 shrink-0">Meses</Label>
                          <div className="relative flex-1">
                            <Input
                              data-testid="input-distribuir-meses"
                              type="number"
                              min="1"
                              max="600"
                              value={formData.distribuir_config?.meses_manual || ''}
                              onChange={(e) => setFormData({ ...formData, distribuir_config: { ...(formData.distribuir_config || {}), meses_manual: parseInt(e.target.value) || 0 } })}
                              className="h-9 text-sm pr-14"
                              placeholder="36"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs">meses</span>
                          </div>
                        </div>
                      )}

                      {/* Botão Gerar */}
                      {(() => {
                        const cfg = formData.distribuir_config || {};
                        const valorDigitado = cfg.tipo_valor === 'percentual'
                          ? valorCompra * ((Number(cfg.percentual) || 0) / 100)
                          : (Number(cfg.valor) || 0);
                        const freqMeses = cfg.frequencia === 'trimestral' ? 3
                          : cfg.frequencia === 'quadrimestral' ? 4
                          : cfg.frequencia === 'semestral' ? 6 : 12;
                        const mesesPeriodo = cfg.periodo === 'total'
                          ? prazo
                          : cfg.periodo === 'manual'
                          ? (Number(cfg.meses_manual) || 0)
                          : (construcaoInfo ? construcaoInfo.mesesConstrucao : 0);
                        const qtdReforcos = mesesPeriodo > 0 ? Math.floor(mesesPeriodo / freqMeses) : 0;

                        // Modo "Percentual" sempre é interpretado como TOTAL (X% do valor de compra dividido entre os reforços).
                        // Modo "Valor" + "total" também divide. Modo "Valor" + "parcela" mantém o valor por reforço.
                        const isModoTotal = cfg.tipo_valor === 'percentual' || cfg.modo_valor === 'total';
                        const valorPorReforco = isModoTotal && qtdReforcos > 0
                          ? Math.round((valorDigitado / qtdReforcos) * 100) / 100
                          : valorDigitado;
                        const totalGeral = isModoTotal ? valorDigitado : valorPorReforco * qtdReforcos;

                        if (cfg.periodo === 'construcao' && !construcaoInfo) {
                          return <p className="text-xs text-amber-700">Preencha a "Conclusão da Obra" para o período de construção.</p>;
                        }
                        if (valorDigitado <= 0) return null;

                        return (
                          <button
                            data-testid="btn-gerar-reforcos"
                            onClick={() => {
                              const freqTipo = cfg.frequencia === 'trimestral' ? 'trimestral'
                                : cfg.frequencia === 'quadrimestral' ? 'quadrimestral'
                                : cfg.frequencia === 'anual' ? 'anual' : 'semestral';
                              const freqDesc = cfg.frequencia === 'trimestral' ? 'Reforço trimestral'
                                : cfg.frequencia === 'quadrimestral' ? 'Reforço quadrimestral'
                                : cfg.frequencia === 'anual' ? 'Reforço anual' : 'Reforço semestral';
                              const novos = Array.from({ length: qtdReforcos }, (_, i) => ({
                                mes: (i + 1) * freqMeses,
                                valor: Math.round(valorPorReforco * 100) / 100,
                                tipo: freqTipo,
                                descricao: freqDesc,
                              }));
                              setFormData({ ...formData, reforcos_custom: novos, _showDistribuir: false });
                            }}
                            className="w-full py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            Gerar {qtdReforcos} reforços de R$ {valorPorReforco.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} = R$ {totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Lista manual de reforços */}
                {(formData.reforcos_custom || []).map((reforco, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-24">
                      <div className="relative">
                        <Input
                          data-testid={`reforco-mes-${index}`}
                          type="number"
                          min="1"
                          max={formData.prazo_meses}
                          value={reforco.mes}
                          onChange={(e) => updateReforcoCustom(index, 'mes', e.target.value)}
                          className="h-9 text-sm pr-9"
                          placeholder="Mês"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs">mês</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs">R$</span>
                        <Input
                          data-testid={`reforco-valor-${index}`}
                          type="text"
                          value={editingReforcoIdx === index ? editReforcoValue : formatCurrency(reforco.valor || 0)}
                          onFocus={() => handleReforcoFocus(index)}
                          onChange={(e) => editingReforcoIdx === index ? handleReforcoInput(e.target.value, index) : updateReforcoCustom(index, 'valor', e.target.value)}
                          onBlur={() => handleReforcoBlur(index)}
                          className="pl-9 h-9 text-sm"
                          placeholder="10.000,00"
                        />
                      </div>
                    </div>
                    <Button
                      data-testid={`reforco-remove-${index}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => removeReforcoCustom(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-9 w-9 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}

                <Button
                  data-testid="btn-add-reforco"
                  variant="outline"
                  size="sm"
                  onClick={addReforcoCustom}
                  className="flex items-center gap-2 text-primary border-primary/30 hover:bg-primary/5"
                >
                  <Plus size={14} />
                  Adicionar Reforço
                </Button>

                {(formData.reforcos_custom || []).length > 0 && (
                  <p className="text-xs text-stone-600 bg-stone-50 p-2 rounded">
                    Total reforços: <strong>R$ {reforcosTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                  </p>
                )}
            </div>

          </div>
        </CardContent>
      </Card>
        <Card className="card-form">
          <CardContent className="p-6">
            <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 h-full">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Landmark size={20} className="text-blue-700" />
                  <h3 className="font-heading font-bold text-blue-900 text-base">Financiamento Pós Obra</h3>
                </div>
                <Select
                  value={tipoFinanciamento}
                  onValueChange={(v) => {
                    setTipoFinanciamento(v);
                    setFormData({ ...formData, tipo_financiamento: v });
                  }}
                >
                  <SelectTrigger
                    data-testid="select-tipo-financiamento"
                    className="h-9 w-[180px] bg-white border-blue-300 text-blue-900 font-medium"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bancario">🏦 Bancário</SelectItem>
                    <SelectItem value="direto">🏗️ Construtora (Direto)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-indigo-100 rounded-lg border border-indigo-200">
                  <p className="text-sm text-indigo-800">
                    {tipoFinanciamento === 'bancario' ? (
                      <>Valor financiado com instituição bancária após a conclusão da obra. As parcelas mensais do comprador param na entrega das chaves e o banco quita o saldo. Esse valor entra no total da proposta.</>
                    ) : (
                      <>Financiamento direto com a construtora. As parcelas mensais do comprador continuam após a entrega das chaves até o final do prazo. Nenhum valor extra entra no total — está incluso nas parcelas.</>
                    )}
                  </p>
                </div>

                {/* Campo só aparece no modo Bancário */}
                {tipoFinanciamento === 'bancario' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="financiamento_bancario" className="text-sm font-semibold text-blue-800">
                        Valor do Financiamento Bancário
                      </Label>
                      <div className="flex gap-1 bg-blue-100 rounded p-0.5">
                        <button
                          onClick={() => setFinanciamentoMode('valor')}
                          className={`text-[10px] px-2 py-1 rounded transition-all ${financiamentoMode === 'valor' ? 'bg-blue-600 text-white' : 'text-blue-700'}`}
                        >
                          R$
                        </button>
                        <button
                          onClick={() => setFinanciamentoMode('percentual')}
                          className={`text-[10px] px-2 py-1 rounded transition-all ${financiamentoMode === 'percentual' ? 'bg-blue-600 text-white' : 'text-blue-700'}`}
                        >
                          %
                        </button>
                      </div>
                    </div>

                    {financiamentoMode === 'valor' ? (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                        <Input
                          id="financiamento_bancario"
                          data-testid="input-financiamento-pos-obra"
                          type="text"
                          value={editingField === 'financiamento_pos_obra' ? editValue : formatCurrency(formData.financiamento_pos_obra || 0)}
                          onFocus={() => handleCurrencyFocus('financiamento_pos_obra')}
                          onChange={(e) => handleCurrencyInput(e.target.value)}
                          onBlur={() => handleCurrencyBlur('financiamento_pos_obra')}
                          className="h-11 pl-10 text-base font-mono"
                          placeholder="0,00"
                        />
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          data-testid="input-financiamento-percentual"
                          type="text"
                          value={getPercValue('financiamento_pos_obra_percentual', formData.financiamento_pos_obra_percentual || 0)}
                          onFocus={() => onPercFocus('financiamento_pos_obra_percentual', formData.financiamento_pos_obra_percentual || 0)}
                          onBlur={() => handlePercentageBlur('financiamento_pos_obra_percentual')}
                          onChange={(e) => handlePercentageChange('financiamento_pos_obra_percentual', e.target.value)}
                          className="pr-8 h-11"
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* SAC / Price e Parâmetros do Banco */}
              {tipoFinanciamento === 'bancario' && (
                <div className="mt-4 space-y-3 p-4 bg-white rounded-lg border border-blue-200">
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">Parâmetros do Financiamento Bancário</p>

                  {/* Sistema de amortização */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-600">Sistema</Label>
                      <Select
                        value={formData.tipo_amortizacao || 'PRICE'}
                        onValueChange={(v) => setFormData({ ...formData, tipo_amortizacao: v })}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PRICE">Price (parcela fixa)</SelectItem>
                          <SelectItem value="SAC">SAC (parcela decrescente)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Taxa de juros anual */}
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-600">Taxa de juros a.a. (%)</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.taxa_juros_anual_financiamento || ''}
                          onChange={(e) => setFormData({ ...formData, taxa_juros_anual_financiamento: parseFloat(e.target.value) || null })}
                          className="h-9 pr-8 text-sm"
                          placeholder="11.60"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">%</span>
                      </div>
                    </div>
                  </div>

                  {/* Adição 2 — Idade do cliente + Prazo máximo */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-600">Idade do cliente (anos)</Label>
                      <Input
                        type="number"
                        min="18"
                        max="80"
                        value={formData.idade_cliente || ''}
                        onChange={(e) => setFormData({ ...formData, idade_cliente: parseInt(e.target.value) || null })}
                        className="h-9 text-sm"
                        placeholder="ex: 35"
                      />
                    </div>
                    {prazoMaxMeses !== null && (
                      <div className="space-y-1">
                        <Label className="text-xs text-stone-600">Prazo máximo disponível</Label>
                        <Input
                          readOnly
                          value={prazoMaxAnos <= 0
                            ? 'Não elegível ao financiamento'
                            : `${prazoMaxAnos} anos (${prazoMaxMeses} meses)`}
                          className={`h-9 text-sm font-semibold ${
                            prazoMaxAnos <= 0 ? 'text-red-600' :
                            prazoMaxAnos < 20 ? 'text-amber-600' : 'text-emerald-700'
                          } bg-stone-50`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Prazo do financiamento */}
                  <div className="space-y-1">
                    <Label className="text-xs text-stone-600">Prazo financiamento (meses)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.prazo_financiamento_meses || ''}
                      onChange={(e) => setFormData({ ...formData, prazo_financiamento_meses: parseInt(e.target.value) || null })}
                      className="h-9 text-sm"
                      placeholder="ex: 360"
                    />
                  </div>

                  {/* Estimativa de Seguro MIP/DFI — calculado automaticamente */}
                  {(() => {
                    const idade = Number(formData.idade_cliente) || 0;
                    const valorFin = Number(formData.financiamento_bancario) || Number(formData.financiamento_pos_obra) || 0;
                    if (!idade || !valorFin) return null;

                    // Tabela simplificada MIP: alíquota mensal por faixa etária (% sobre saldo devedor)
                    // Baseada em médias praticadas pela CEF/Bradesco/Itaú
                    const faixaMIP = idade < 26 ? 0.0152 :
                                     idade < 31 ? 0.0170 :
                                     idade < 36 ? 0.0220 :
                                     idade < 41 ? 0.0280 :
                                     idade < 46 ? 0.0370 :
                                     idade < 51 ? 0.0510 :
                                     idade < 56 ? 0.0690 :
                                     idade < 61 ? 0.0960 :
                                     idade < 66 ? 0.1350 : 0.1900; // % a.m. sobre saldo

                    const mipMensal = (valorFin * faixaMIP) / 100;
                    // DFI: fixo ~0.0025% ao mês sobre o valor do imóvel (dano físico)
                    const valorImovel = Number(formData.valor_compra) || valorFin;
                    const dfiMensal = (valorImovel * 0.0025) / 100;
                    const totalSeguro = mipMensal + dfiMensal;

                    return (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-amber-800">Estimativa de Seguro (MIP + DFI)</span>
                          <span className="text-xs text-amber-600 font-mono font-bold">
                            R$ {totalSeguro.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mês
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[10px] text-amber-700">
                          <div>
                            <span className="font-medium">MIP</span> (vida, {idade} anos)
                            <br />
                            <span className="font-mono">R$ {mipMensal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mês</span>
                          </div>
                          <div>
                            <span className="font-medium">DFI</span> (dano físico)
                            <br />
                            <span className="font-mono">R$ {dfiMensal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mês</span>
                          </div>
                        </div>
                        <p className="text-[10px] text-amber-600 leading-snug">
                          ⚠ Estimativa referencial com base em tabelas médias de mercado. O valor real varia por seguradora, banco e perfil do tomador. Não incluso nos cálculos de parcela.
                        </p>
                      </div>
                    );
                  })()}

                  {!formData.idade_cliente && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                      ⚠ Preencha a idade do cliente acima para estimar o seguro MIP/DFI.
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ========== BLOCO: ANÁLISE FINANCEIRA (TIR/VPL) — taxa de desconto ========== */}
        <Card className="card-form border-emerald-200">
          <CardContent className="p-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 size={16} className="text-emerald-600" />
                <h3 className="font-heading text-sm font-bold text-emerald-800">Análise Financeira</h3>
                <span className="text-[10px] text-stone-400 uppercase tracking-wider ml-1">TIR · VPL · Custo de Oportunidade</span>
              </div>
              <p className="text-xs text-stone-500">Taxa usada para calcular o VPL e comparar com a renda fixa. Deixe em branco para usar o CDI estimado automaticamente.</p>

              {/* Momento de Saída */}
              <div className="space-y-1.5">
                <Label className="text-xs text-stone-600 font-semibold">Momento de Saída do Investimento</Label>
                <p className="text-[10px] text-stone-400">Define o horizonte do benchmark — CDI/poupança/FII calculados para esse período.</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { value: 'meio_obra',    label: '🏗 Meio de obra' },
                    { value: 'final_obra',   label: '🏛 Final de obra' },
                    { value: 'pos_chaves',   label: '🔑 Pós-chaves' },
                    { value: 'pos_quitacao', label: '✅ Após quitação' },
                  ].map(op => (
                    <button
                      key={op.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, momento_saida: op.value })}
                      className={`text-xs px-3 py-2 rounded-lg border text-left transition-all ${
                        (formData.momento_saida || 'pos_chaves') === op.value
                          ? 'bg-emerald-700 text-white border-emerald-700 font-semibold shadow-sm'
                          : 'bg-white text-stone-600 border-stone-200 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                    >
                      {op.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-stone-600">Índice de referência</Label>
                  <Select
                    value={formData.indice_desconto || 'CDI'}
                    onValueChange={(v) => setFormData({ ...formData, indice_desconto: v })}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CDI">CDI</SelectItem>
                      <SelectItem value="SELIC">SELIC</SelectItem>
                      <SelectItem value="IPCA">IPCA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-600">Taxa a.a. (% — opcional)</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.taxa_desconto_anual || ''}
                      onChange={(e) => setFormData({ ...formData, taxa_desconto_anual: parseFloat(e.target.value) || null })}
                      className="h-9 pr-8 text-sm"
                      placeholder="automático"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== BLOCO: PARCELA DAS CHAVES ========== */}
        <Card className="card-form">
          <CardContent className="p-6">
            <div className="space-y-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-amber-300 h-full">
              <div className="flex items-center gap-2 mb-3">
                <Key size={20} className="text-amber-700" />
                <h3 className="font-heading font-bold text-amber-900 text-base">Parcela das Chaves</h3>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-amber-100 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    Balão pago na entrega das chaves (conclusão da obra). Parcela maior que quita o saldo restante antes do financiamento bancário assumir.
                  </p>
                </div>

                {/* Campo de valor/percentual da Parcela das Chaves */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="parcela_chaves" className="text-sm font-semibold text-amber-800">
                      Valor da Parcela das Chaves
                    </Label>
                    <div className="flex gap-1 bg-amber-100 rounded p-0.5">
                      <button
                        onClick={() => setParcelaChavesMode('valor')}
                        className={`text-[10px] px-2 py-1 rounded transition-all ${parcelaChavesMode === 'valor' ? 'bg-amber-600 text-white' : 'text-amber-700'}`}
                      >
                        R$
                      </button>
                      <button
                        onClick={() => setParcelaChavesMode('percentual')}
                        className={`text-[10px] px-2 py-1 rounded transition-all ${parcelaChavesMode === 'percentual' ? 'bg-amber-600 text-white' : 'text-amber-700'}`}
                      >
                        %
                      </button>
                    </div>
                  </div>

                  {parcelaChavesMode === 'valor' ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R$</span>
                      <Input
                        id="parcela_chaves"
                        data-testid="input-parcela-chaves"
                        type="text"
                        value={editingField === 'parcela_chaves_valor' ? editValue : formatCurrency(formData.parcela_chaves_valor || 0)}
                        onFocus={() => handleCurrencyFocus('parcela_chaves_valor')}
                        onChange={(e) => handleCurrencyInput(e.target.value)}
                        onBlur={() => handleCurrencyBlur('parcela_chaves_valor')}
                        className="h-11 pl-10 text-base font-mono"
                        placeholder="0,00"
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <Input
                        data-testid="input-parcela-chaves-percentual"
                        type="text"
                        value={getPercValue('parcela_chaves_percentual', formData.parcela_chaves_percentual || 0)}
                        onFocus={() => onPercFocus('parcela_chaves_percentual', formData.parcela_chaves_percentual || 0)}
                        onBlur={() => handlePercentageBlur('parcela_chaves_percentual')}
                        onChange={(e) => handlePercentageChange('parcela_chaves_percentual', e.target.value)}
                        className="pr-8 h-11"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
                    </div>
                  )}
                  {formData.valor_compra > 0 && (formData.parcela_chaves_valor > 0 || formData.parcela_chaves_percentual > 0) && (
                    <p className="text-[11px] text-amber-700">
                      {parcelaChavesMode === 'valor'
                        ? `Equivale a ${((formData.parcela_chaves_valor / formData.valor_compra) * 100).toFixed(2)}% do valor de compra`
                        : `Equivale a R$ ${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format((formData.valor_compra * formData.parcela_chaves_percentual) / 100)}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========== BLOCO SEPARADO: PRÉVIA DA PROPOSTA ========== */}
      {valorCompra > 0 && (
        <Card className="card-form">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Calculator size={20} className="text-primary" />
                <h3 className="font-heading font-bold text-primary text-base">Prévia da Proposta</h3>
              </div>
              
              {/* Grid principal: Ato + Parcelas + Reforços + Veículos (se houver) + Parcela das Chaves (se houver) + Financiamento (se houver) + Total */}
              {(() => {
                const hasFinanciamento = tipoFinanciamento === 'bancario' && financiamento > 0;
                const hasChaves = parcelaChavesTotal > 0;
                const hasVeiculos = veiculosTotal > 0;
                const extras = (hasFinanciamento ? 1 : 0) + (hasChaves ? 1 : 0) + (hasVeiculos ? 1 : 0);
                // Tailwind precisa de classes literais (sem interpolação) para o purge funcionar
                const gridClass = extras >= 3
                  ? 'grid gap-3 grid-cols-2 md:grid-cols-7'
                  : extras === 2
                    ? 'grid gap-3 grid-cols-2 md:grid-cols-6'
                    : extras === 1
                      ? 'grid gap-3 grid-cols-2 md:grid-cols-5'
                      : 'grid gap-3 grid-cols-2 md:grid-cols-4';
                return (
              <div className={gridClass}>
                <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-[10px] uppercase tracking-wider text-red-700 mb-1">Ato/Sinal</p>
                  <p className="text-base font-mono font-semibold text-red-800">
                    R$ {atoSinal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-red-600">{fmtPct(atoPerc, 1)}%</p>
                </div>
                
                <div className="text-center p-3 bg-stone-100 rounded-lg border border-stone-200">
                  <p className="text-[10px] uppercase tracking-wider text-stone-600 mb-1">Parcelas</p>
                  <p className="text-base font-mono font-semibold text-stone-800">
                    R$ {parcelasTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-stone-500">{fmtPct(parcelasPerc, 1)}%</p>
                </div>
                
                <div className="text-center p-3 bg-stone-100 rounded-lg border border-stone-200">
                  <p className="text-[10px] uppercase tracking-wider text-stone-600 mb-1">
                    Reforços
                  </p>
                  <p className="text-base font-mono font-semibold text-stone-800">
                    R$ {reforcosTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-stone-500">{fmtPct(reforcosPerc, 1)}%</p>
                </div>

                {/* Card Veículos no Negócio - aparece quando há veículos */}
                {hasVeiculos && (
                  <div className="text-center p-3 bg-sky-50 rounded-lg border border-sky-200">
                    <p className="text-[10px] uppercase tracking-wider text-sky-700 mb-1">Veículos</p>
                    <p className="text-base font-mono font-semibold text-sky-800">
                      R$ {veiculosTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[10px] text-sky-600">{fmtPct(veiculosPerc, 1)}%</p>
                  </div>
                )}

                {/* Card Parcela das Chaves - aparece quando configurada */}
                {hasChaves && (
                  <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">Parc. Chaves</p>
                    <p className="text-base font-mono font-semibold text-amber-800">
                      R$ {parcelaChavesTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[10px] text-amber-600">{fmtPct(parcelaChavesPerc, 1)}%</p>
                  </div>
                )}

                {/* Card de Financiamento Bancário - aparece na grid quando ativo */}
                {hasFinanciamento && (() => {
                  const percFinanciamento = valorCompra > 0 ? (financiamento / valorCompra * 100) : 0;
                  return (
                    <div className="text-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                      <p className="text-[10px] uppercase tracking-wider text-indigo-700 mb-1">Financ. Pós Obra</p>
                      <p className="text-base font-mono font-semibold text-indigo-800">
                        R$ {financiamento.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-[10px] text-indigo-600">{fmtPct(percFinanciamento, 1)}%</p>
                    </div>
                  );
                })()}
                
                <div className="text-center p-3 bg-emerald-900 rounded-lg">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-200 mb-1">Total</p>
                  <p className="text-base font-mono font-semibold text-white">
                    R$ {totalProposta.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-emerald-300">
                    {valorCompra > 0 ? fmtPct((totalProposta / valorCompra * 100), 1) : '0'}%
                  </p>
                </div>
              </div>
                );
              })()}

              {/* Aviso de Falta/Excede (tolerância de R$ 1,00 para erro de arredondamento) */}
              {Math.abs(totalProposta - valorCompra) >= 1 && valorCompra > 0 && (
                <div className={`mt-3 text-center p-3 rounded-lg border-2 ${totalProposta > valorCompra ? 'bg-amber-50 border-amber-300' : 'bg-red-100 border-red-500 animate-pulse'}`}>
                  <p className={`text-[10px] uppercase tracking-wider mb-1 font-bold ${totalProposta > valorCompra ? 'text-amber-700' : 'text-red-700'}`}>
                    {totalProposta > valorCompra ? 'Excede' : 'FALTA'}
                  </p>
                  <p className={`text-lg font-mono font-bold ${totalProposta > valorCompra ? 'text-amber-700' : 'text-red-600'}`}>
                    R$ {Math.abs(totalProposta - valorCompra).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  {totalProposta < valorCompra && (
                    <p className="text-[10px] text-red-600 font-semibold mt-1">
                      {fmtPct(((valorCompra - totalProposta) / valorCompra * 100), 1)}% do valor
                    </p>
                  )}
                </div>
              )}
              {/* Indicador "fechado" quando dentro da tolerância */}
              {Math.abs(totalProposta - valorCompra) < 1 && valorCompra > 0 && (
                <div className="mt-3 text-center p-3 rounded-lg border-2 bg-emerald-50 border-emerald-300">
                  <p className="text-[10px] uppercase tracking-wider mb-1 font-bold text-emerald-700">
                    Proposta Fechada ✓
                  </p>
                  <p className="text-sm text-emerald-700">
                    Total cobre 100% do valor de compra
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Botão de Calcular */}
      <button
        data-testid="btn-calcular"
        onClick={onCalculate}
        disabled={loading}
        className="btn-primary w-full h-12 text-base flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="loading-spinner" />
            Calculando...
          </>
        ) : (
          <>
            <RefreshCw size={18} />
            Calcular Investimento
          </>
        )}
      </button>

      {showClienteModal && (
        <ClientModal
          onClose={() => setShowClienteModal(false)}
          onSuccess={(newClient) => {
            if (newClient?.name) setFormData({ ...formData, nome_cliente: newClient.name });
            setClientesCadastrados(prev => [...prev, newClient]);
            setShowClienteModal(false);
          }}
        />
      )}
    </div>
  );
};

export default InvestmentForm;
